/**
 * Parsing never throws on malformed input. Anything the parser cannot represent
 * becomes a diagnostic and the construct is skipped, so one broken shape cannot
 * cost a lecturer the other ninety-nine slides.
 */
export type DiagnosticSeverity = 'info' | 'warning' | 'error' | 'blocking';

export interface ParseDiagnostic {
  readonly severity: DiagnosticSeverity;
  /** Stable machine-readable code, e.g. `media.linked-not-embedded`. */
  readonly code: string;
  readonly message: string;
  readonly slideIndex?: number;
  readonly shapeName?: string;
  readonly partName?: string;
}

export function diagnostic(
  severity: DiagnosticSeverity,
  code: string,
  message: string,
  where: Pick<ParseDiagnostic, 'slideIndex' | 'shapeName' | 'partName'> = {},
): ParseDiagnostic {
  return { severity, code, message, ...where };
}

/** Collects diagnostics without letting any single call site throw. */
export class DiagnosticSink {
  readonly #items: ParseDiagnostic[] = [];

  add(d: ParseDiagnostic): void {
    this.#items.push(d);
  }

  report(
    severity: DiagnosticSeverity,
    code: string,
    message: string,
    where: Pick<ParseDiagnostic, 'slideIndex' | 'shapeName' | 'partName'> = {},
  ): void {
    this.#items.push(diagnostic(severity, code, message, where));
  }

  /**
   * Runs `fn`, converting any thrown error into a diagnostic and returning
   * `fallback`. This is the only place the parser is allowed to swallow an
   * exception.
   */
  guard<T>(
    code: string,
    where: Pick<ParseDiagnostic, 'slideIndex' | 'shapeName' | 'partName'>,
    fallback: T,
    fn: () => T,
  ): T {
    try {
      return fn();
    } catch (error) {
      this.report(
        'error',
        code,
        error instanceof Error ? error.message : String(error),
        where,
      );
      return fallback;
    }
  }

  get items(): readonly ParseDiagnostic[] {
    return this.#items;
  }

  get blocking(): readonly ParseDiagnostic[] {
    return this.#items.filter((d) => d.severity === 'blocking');
  }

  get hasBlocking(): boolean {
    return this.#items.some((d) => d.severity === 'blocking');
  }
}
